// src/pages/admin/FCMMessaging.jsx
import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';

const predefinedTopics = [
  'all_users',
  'admins',
  'delivery_riders',
  'store_managers',
  'packers',
  'coop_members',
  'general_announcements',
  'order_updates',
  'promotions',
  'system_maintenance'
];

export default function FCMMessaging() {
  const [topic, setTopic] = useState('all_users');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [dataJson, setDataJson] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const parseData = () => {
    if (!dataJson.trim()) return {};
    try {
      const obj = JSON.parse(dataJson);
      // Convert all values to strings as FCM data requires strings
      const out = {};
      Object.entries(obj).forEach(([k, v]) => { out[k] = String(v); });
      return out;
    } catch (e) {
      alert('Invalid JSON in Data payload');
      throw e;
    }
  };

  const send = async () => {
    if (!topic.trim()) {
      alert('Topic is required');
      return;
    }
    if (!title.trim() && !body.trim()) {
      if (!window.confirm('Send without title/body?')) return;
    }

    setSending(true);
    setResult(null);
    try {
      const callable = httpsCallable(functions, 'sendTopicMessage');
      const payload = {
        topic: topic.trim(),
        notification: {
          title: title || undefined,
          body: body || undefined,
          imageUrl: imageUrl || undefined,
        },
        webpush: linkUrl ? { fcmOptions: { link: linkUrl } } : undefined,
        data: parseData(),
      };
      const res = await callable(payload);
      setResult(res?.data || { ok: true });
    } catch (e) {
      console.error('sendTopicMessage error', e);
      setResult({ ok: false, error: e?.message || String(e) });
      alert('Failed to send message: ' + (e?.message || e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="text-xl font-semibold mb-4">FCM Messaging (Topics)</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Topic</label>
          <div className="flex gap-2 mb-2 flex-wrap">
            {predefinedTopics.map(t => (
              <button
                key={t}
                className={`px-3 py-1 rounded border text-sm ${topic===t? 'bg-brand-600 text-white border-brand-600':'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                onClick={() => setTopic(t)}
                disabled={sending}
              >
                {t}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value.replace(/\s+/g, '_').toLowerCase())}
            className="w-full px-3 py-2 border rounded"
            placeholder="custom_topic"
            disabled={sending}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Title</label>
          <input type="text" value={title} onChange={(e)=>setTitle(e.target.value)} className="w-full px-3 py-2 border rounded" placeholder="Notification title" disabled={sending}/>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">Body</label>
          <textarea value={body} onChange={(e)=>setBody(e.target.value)} className="w-full px-3 py-2 border rounded" rows={3} placeholder="Notification body" disabled={sending}/>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Image URL (optional)</label>
          <input type="url" value={imageUrl} onChange={(e)=>setImageUrl(e.target.value)} className="w-full px-3 py-2 border rounded" placeholder="https://..." disabled={sending}/>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Link URL (optional, webpush)</label>
          <input type="url" value={linkUrl} onChange={(e)=>setLinkUrl(e.target.value)} className="w-full px-3 py-2 border rounded" placeholder="https://..." disabled={sending}/>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-1">Data (JSON, values will be stringified)</label>
          <textarea value={dataJson} onChange={(e)=>setDataJson(e.target.value)} className="w-full px-3 py-2 border rounded font-mono text-sm" rows={4} placeholder='{"deepLink":"/store", "campaign":"promo1"}' disabled={sending}/>
        </div>
      </div>

      <div className="mt-4 flex gap-3">
        <button onClick={send} disabled={sending} className="px-4 py-2 bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50">
          {sending ? 'Sending…' : 'Send to Topic'}
        </button>
        <button onClick={()=>{setTitle('');setBody('');setImageUrl('');setLinkUrl('');setDataJson('');}} disabled={sending} className="px-4 py-2 border rounded">
          Clear
        </button>
      </div>

      {result && (
        <div className="mt-4 p-3 bg-gray-50 border rounded text-sm">
          <pre className="whitespace-pre-wrap">{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}

      <div className="mt-6 text-xs text-gray-600">
        <p>Notes:</p>
        <ul className="list-disc ml-5 space-y-1">
          <li>Topics must match those your clients subscribe to (e.g., all_users, delivery_riders).</li>
          <li>Web push link opens when the notification is clicked (if supported).</li>
          <li>Data payload values are sent as strings.</li>
        </ul>
      </div>
    </div>
  );
}
